import { useEffect, type FC, useState } from "react"

import { MODULE_POWUR_OS_EQUIPMENT_ID } from "@logic/lib/powur-os"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { activePanelIndex } from "~/containers/LegacyDesigner/state/elements/panels"
import { usePublicPresentation } from "~/containers/PublicPresentation/public-presentation-context"
import { useGetEquipmentForProposalQuery } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { replaceCharsForCY } from "~/utils/replaceCharsForCY"

import { DEVICE } from "../../../../constants/device-widths"
import {
  brochureState,
  offsetAsPercentage,
} from "../../state/brochure/brochure-state"
import { useDesignContext } from "../../state/designs/design-context"
import {
  systemSizeInKwState,
  storageNameState,
} from "../../state/designs/design-state"
import { useDispatchError } from "../../state/notifications/QueryErrorModal"

const SideBar = styled.aside`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 20;
  background-color: rgb(255, 255, 255);
  position: relative;
  width: 100%;
  @media ${DEVICE.laptop} {
    width: 300px;
  }
  > div {
    display: flex;
    height: 100%;
    @media print {
      height: fit-content;
    }
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    flex-direction: row;
    @media ${DEVICE.laptop} {
      flex-wrap: nowrap;
      flex-direction: column;
    }
    > * ~ * {
      border-top: 1px solid #e5e5e5;
    }
  }
`
const MenuItem = styled.div<{ clickable: boolean }>`
  cursor: ${({ clickable }) => (clickable ? `pointer` : `default`)};
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px;
  width: 50%;
  height: 150px;
  @media ${DEVICE.laptop} {
    width: 65%;
    height: 100%;
  }
  @media print {
    height: 100px;
  }
  :nth-of-type(odd):not(:last-of-type) {
    border-right: 1px solid #e5e5e5;
  }
  @media ${DEVICE.laptop} {
    :nth-of-type(odd):not(:last-of-type) {
      border-right: none;
    }
  }
  span {
    text-align: center;
    font-weight: 600;
    color: var(--blue);
  }
  label {
    text-transform: capitalize;
    font-size: 14px;
  }
`

export const addCommas = (num: number | string): string =>
  Number(num).toLocaleString()

const unavailableMetric = `—`

const formatAuroraMetric = (
  value: number | null,
  options: Intl.NumberFormatOptions,
  unit: string,
): string =>
  value === null
    ? unavailableMetric
    : `${Intl.NumberFormat(`en-US`, options).format(value)}${unit}`

const Sidebar: FC = () => {
  const publicPresentation = usePublicPresentation()
  const [equipment, setEquipment] = useState<
    Array<{ id: number; name: string }>
  >(publicPresentation?.equipment ?? [])
  const [inverterName, setInverterName] = useState<string>(``)
  const [moduleName, setModuleName] = useState<string>(``)
  const [storageName, setStorageName] = useState<string>(``)
  const navigate = useNavigate()
  const systemSize = useRecoilValue<number>(systemSizeInKwState)
  const offset = useRecoilValue(offsetAsPercentage)
  const brochure = useRecoilValue(brochureState)
  const { annualSolarProduction } = brochure
  const { t } = useTranslation()
  const activePanelCount = useRecoilValue(activePanelIndex).length
  const storage = useRecoilValue(storageNameState)

  const { client } = useGraphqlClient()
  const design = useDesignContext()
  const onError = useDispatchError()
  const isDesignLoaded = Boolean(design.local.id)
  const isBrochureLoaded = Boolean(brochure.id)
  const isAuroraDesign = isDesignLoaded && design.local.source === `aurora`
  const panelCount = isAuroraDesign
    ? (design.local.designEquipment.find(
        (designEquipment) =>
          designEquipment.powurOsEquipmentTypeId ===
          MODULE_POWUR_OS_EQUIPMENT_ID,
      )?.quantity ?? 0)
    : activePanelCount

  const systemSizeInfo = !isDesignLoaded
    ? unavailableMetric
    : isAuroraDesign
      ? formatAuroraMetric(
          design.local.systemSizeKw,
          { maximumFractionDigits: 2 },
          ` kW`,
        )
      : `${addCommas(systemSize)} kW`
  const energyOffsetInfo = !isDesignLoaded
    ? unavailableMetric
    : isAuroraDesign
      ? formatAuroraMetric(
          design.local.annualOffset,
          { maximumFractionDigits: 0 },
          `%`,
        )
      : isBrochureLoaded
        ? offset
        : unavailableMetric
  const productionInfo = !isDesignLoaded
    ? unavailableMetric
    : isAuroraDesign
      ? formatAuroraMetric(
          design.local.annualProduction,
          { maximumFractionDigits: 0 },
          ` kWh`,
        )
      : isBrochureLoaded
        ? `${annualSolarProduction.toLocaleString(`en-US`)} kWh`
        : unavailableMetric

  const shouldFetchEquipment =
    !publicPresentation && Boolean(design.local.proposalId)

  const equipmentQuery = useGetEquipmentForProposalQuery(
    client,
    {
      proposalId: design.local.proposalId,
      equipmentType: null,
      inverterManufacturerId: null,
    },
    {
      onError,
      enabled: shouldFetchEquipment,
    },
  )

  useEffect(() => {
    if (!design.local.proposalId) {
      return
    }
    if (!equipmentQuery.data?.equipmentForProposal) {
      return
    }

    const { equipmentForProposal } = equipmentQuery.data
    setEquipment(equipmentForProposal)
  }, [equipmentQuery.data?.equipmentForProposal, design.local.proposalId])

  useEffect(() => {
    if (!Array.isArray(equipment) || equipment.length === 0) return

    const designInverterId = design?.local?.inverter?.id
    if (!designInverterId) return

    const inverter = equipment.find((inv) => inv.id === designInverterId)
    if (inverter?.name) setInverterName(inverter.name)
  }, [equipment, design.local.inverter])

  useEffect(() => {
    if (!Array.isArray(equipment) || equipment.length === 0) return

    const designModuleId = design.local.designEquipment?.find(
      (designEquipment) =>
        designEquipment.powurOsEquipmentTypeId === MODULE_POWUR_OS_EQUIPMENT_ID,
    )?.equipment?.id

    if (!designModuleId) {
      setModuleName(``)
      return
    }

    const solarModule = equipment.find((inv) => inv.id === designModuleId)
    if (solarModule?.name) setModuleName(solarModule.name)
  }, [equipment, design.local.designEquipment])

  useEffect(() => {
    if (!Array.isArray(equipment) || equipment.length === 0) return

    const storageModuleId = design?.local?.energyBackup?.id
    if (!storageModuleId) {
      setStorageName(``)
      return
    }

    const storage = equipment.find((inv) => inv.id === storageModuleId)
    if (storage?.name) setStorageName(storage.name)
  }, [equipment, design.local.energyBackup])

  const systemInfo = [
    {
      info: systemSizeInfo,
      label: t(`systemSize`),
    },
    {
      info: energyOffsetInfo,
      label: t(`energyOffset`),
    },
    {
      info: productionInfo,
      label: t(`production`),
    },
    {
      info: moduleName,
      label: t(`moduleType`),
      form: `setup/equipment-selection/module-selection`,
    },
    {
      info: inverterName,
      label: t(`inverter`),
      form: `setup/equipment-selection/inverter-selection`,
    },
    {
      info: panelCount,
      label: t(`numberOfPanels`),
    },
  ].concat(storageName !== `` ? { info: storageName, label: t(`storage`) } : [])

  return (
    <SideBar>
      <div>
        {systemInfo.map((info, index) => (
          <MenuItem
            key={index}
            clickable={!publicPresentation && Boolean(info.form)}
            onClick={
              !publicPresentation && info.form
                ? () => navigate(info.form)
                : undefined
            }
          >
            <span data-testid={`your-system-${replaceCharsForCY(info.label)}`}>
              {info.info}
            </span>
            <label>{info.label}</label>
          </MenuItem>
        ))}
      </div>
    </SideBar>
  )
}

export default Sidebar
